<script setup>
import { WdsSearchFormInline, WdsSearchFormStandalone, WdsSearchFormWizard } from '@wds/components';
import searchConfig from '@/config/search';
import { replayActiveSearchFocus } from '@/utils/replayActiveSearchFocus.js';

// ### STORE/PINIA
const searchStore = useSearchStore();
const testingStore = useTestingStore();

// ### COMPOSABLES
const dateTime = useDateTime();
const searchData = useSearch();

// ### DATA
const inlineSearchForm = ref(null);
const phoneSearchForm = ref(null);
const searchFormRecentSearches = ref(null);

// ### FEATURE TOGGLES
const newAutoCompleteServiceEnabled = testingStore.hasEnabledFeature('web_newAutoCompleteService');

// ### COMPUTED
const searchFormInlineParams = computed(() => ({
  size: 'large',
  destinationMaxVisibleSearchResults: newAutoCompleteServiceEnabled ? 5 : 3,
  ...searchData.getSearchFormCommonParams.value,
  destinationRecentSearches: searchFormRecentSearches.value,
}));

const searchFormStandaloneParams = computed(() => ({ translations: searchData.getSearchFormTranslations.value }));

const searchFormWizardParams = computed(() => ({
  activeStep: searchData.wizardActiveStep.value,
  openWizard: searchData.wizardIsOpen.value,
  focusDestinationOnOpen: true,
  destinationMaxVisibleSearchResults: 0,
  ...searchData.getSearchFormCommonParams.value,
  destinationRecentSearches: searchFormRecentSearches.value,
}));

// ### CREATED
searchStore.setSearchCheckIn(dateTime.tomorrow);
searchStore.setSearchCheckOut(dateTime.addDaysToDate(dateTime.tomorrow, searchConfig.initialNights));

onMounted(() => {
  replayActiveSearchFocus({
    activeElement: document.activeElement,
    inlineRoot: inlineSearchForm.value?.$el,
    phoneRoot: phoneSearchForm.value?.$el,
    onPhone: () => searchData.handleWizardOpen(),
  });

  // Restore previously inputted values if the page was reloaded (e.g. by the TrustArc consent banner).
  const restored = searchStore.restoreSearchParamsFromStorage();
  if (restored) {
    return;
  }

  // Override default check-in and check-out dates on client side because the user might be in a different timezone
  // than the server.
  searchStore.setSearchCheckIn(dateTime.tomorrow);
  searchStore.setSearchCheckOut(dateTime.addDaysToDate(dateTime.tomorrow, searchConfig.initialNights));

  // Get recent searches for the form on client side because the user might be in a different timezone
  // than the server and avoid hydration mismatch.
  searchFormRecentSearches.value = searchData.getUserRecentSearchesForForm?.value ?? null;
});
</script>

<template>
  <WdsSearchFormInline
    ref="inlineSearchForm"
    class="hp-search-form-desktop"
    v-bind="searchFormInlineParams"
    @destination-selected="searchData?.handleDestinationFieldClicked"
    @destination-input="searchData?.handleDestinationSearch"
    @destination-item-selected="searchData?.handleDestinationItemSelected"
    @destination-more-items-selected="searchData?.handleDestinationMoreItemsSelected"
    @recent-destination-selected="searchData?.handleRecentDestinationItemSelected"
    @current-location="searchData?.handleCurrentLocation"
    @dates-selected="searchData?.handleDatesFieldClicked"
    @dates-changed="searchData?.handleDatesSelected"
    @guests-selected="searchData?.handleGuestsFieldClicked"
    @guests-change="searchData?.handleGuestsNumberChanged"
    @guests-group-type-change="searchData?.handleGuestsGroupTypeChanged"
    @guests-group-ages-change="searchData?.handleGuestsGroupAgesChanged"
    @search-form-submit="searchData?.handleSubmit"
  />
  <WdsSearchFormStandalone
    ref="phoneSearchForm"
    class="hp-search-form-mobile"
    v-bind="searchFormStandaloneParams"
    @input-selected="searchData?.handleWizardOpen"
  />
  <ClientOnly>
    <WdsSearchFormWizard
      v-bind="searchFormWizardParams"
      @destination-selected="searchData?.handleDestinationItemSelected"
      @destination-input="searchData?.handleDestinationSearch"
      @destination-item-selected="searchData?.handleDestinationItemSelected"
      @destination-more-items-selected="searchData?.handleDestinationMoreItemsSelected"
      @recent-destination-selected="searchData?.handleRecentDestinationItemSelected"
      @current-location="searchData?.handleCurrentLocation"
      @guests-change="searchData?.handleGuestsNumberChanged"
      @guests-group-type-change="searchData?.handleGuestsGroupTypeChanged"
      @guests-group-ages-change="searchData?.handleGuestsGroupAgesChanged"
      @search-form-submit="searchData?.handleSubmit"
      @search-form-close="searchData?.handleWizardClose"
    />
  </ClientOnly>
</template>

<style scoped lang="scss">
.hp-search-form-desktop {
  display: none;
}

.hp-search-form-mobile {
  display: flex;
  flex: 1;
}

@include tablet-large {
  // LARGE TABLET RULES
  .hp-search-form-desktop {
    display: block;
  }

  .hp-search-form-mobile {
    display: none !important;
  }
}
</style>
